/** @jsx jsx */

import React, { useState, useContext, useEffect } from 'react';
import { css as _css } from 'emotion';
import { css, jsx } from '@emotion/core';
import { colors, getButtonFocus } from 'styles/variables';
import getBlockById from 'services/apiHelpers/getBlockById';
import { interactionContext } from './interactionContext';
import { useSelector } from 'react-redux';
import getInteractionsInTopic from 'services/apiHelpers/getInteractionsInTopic';
import hexToRGB from 'services/hexToRGB';
import getCurrentTopic from 'services/apiHelpers/getCurrentTopic';
import pubSub from 'services/pubSub';
import TopicIcon from './TopicIcon';
import getTopicSId from 'services/getTopicSId';

const { lGrey0, lPink } = colors;

const ProgressBar = ({ color, percentComplete }) => {
  const width = 130;
  const height = 8;

  const { r, g, b } = hexToRGB(color);

  return (
    <div
      css={css`
        position: relative;
        width: ${width}px;
        height: ${height}px;
        border-radius: 9999px;
        background: rgba(${r}, ${g}, ${b}, 0.3);
        transition: background 0.1s;
      `}
    >
      <div
        css={css`
          position: absolute;
          left: 0;
          top: 0;
          width: ${width * (percentComplete / 100)}px;
          height: ${height}px;
          border-radius: 9999px;
          background: rgb(${r}, ${g}, ${b});
          transition: background 0.1s;
        `}
      />
    </div>
  );
};

const TopicPanel = ({ topic, updateTopicComplete }) => {
  const context: any = useContext(interactionContext);
  const { id: interactionId } = context;

  const {
    graphColor = '#61cece',
    cardColor = '#fff',
    textColor = '#1e2533',
    navigateToTopic = true,
    addImage = true,
    cardWidth = 235,
    cardHeight = 150,
  } = context.props;

  let { font = '' } = context.props;

  const { focusedNodeId, theme, interactionsSeen } = useSelector(
    (state: any) => state
  );

  const [iconSelected, setIconSelected] = useState<Boolean>(false);

  const { id } = topic;
  const { name, topicIcon = {} } = topic.props;

  const isEditing = focusedNodeId === interactionId;

  const cardSize = cardWidth === 235 ? 'small' : 'large';

  let navigationEnabled =
    !isEditing && navigateToTopic && getCurrentTopic()!.id !== id;

  const navigate = navigationEnabled
    ? () => {
        if (window.dev && window.dev()) {
          return;
        }

        const targetLocation = getTopicSId(topic);

        if (targetLocation) {
          window.location.hash = targetLocation;
        }
      }
    : null;

  // nuclear
  if (window.dev()) {
    navigationEnabled = false;
  }

  const interactionsInTopic = getInteractionsInTopic(topic)
    .filter((i: any) => i?.meta?.subType !== 'TopicPortal')
    .map((i: any) => i.id);

  const interactionsInTopicSeen = interactionsInTopic.filter((i: any) => {
    return (
      interactionsSeen
        .filter((i: any) => {
          return i && getBlockById(i)?.meta?.subType !== 'TopicPortal';
        })
        // .map((i: any) => i.replace('i', ''))
        .includes(i)
    );
  });

  let percentComplete = Number(
    (
      (interactionsInTopicSeen.length / interactionsInTopic.length) *
      100
    ).toFixed()
  );

  if (isNaN(percentComplete)) {
    percentComplete = 0;
  }

  // update font : theme > local
  // does the section get a font ?
  font = !font ? theme?.fontFamily : font;
  //

  useEffect(() => {
    const pS = pubSub.subscribe('UPDATE_TOPIC_ICON', $ => {
      if (!$.topic) {
        return;
      }
      $.topic.id !== id && setIconSelected(false);
    });

    return () => pS();
  }, []);

  useEffect(() => {
    iconSelected &&
      pubSub.publish(
        'UPDATE_TOPIC_ICON',
        iconSelected
          ? {
              topic,
            }
          : null
      );
  }, [iconSelected]);

  useEffect(() => {
    if (percentComplete === 100) {
      updateTopicComplete(topic.id);
    }
  }, [percentComplete]);

  const TopicNameContainer = ({ children }) => {
    const shared = `
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    left: 0;
    width: 100%;
    `;
    return !!addImage ? (
      <div
        css={css`
          ${shared}
          top: ${cardSize === 'small' ? '40px' : '55px'};
          height: calc(100% - ${cardSize === 'small' ? '95px' : '110px'});
        `}
      >
        {children}
      </div>
    ) : (
      <div
        css={css`
          ${shared}
          top: 20px;
          height: calc(100% - 75px);
        `}
      >
        {children}
      </div>
    );
  };

  return interactionsInTopic.length === 0 ? null : (
    <div
      css={css`
        position: relative;
        width: ${cardWidth}px;
        margin: 20px 15px;
      `}
    >
      <a
        href={`#${getTopicSId(topic)}`}
        title={`Go to ${name}`}
        target="_self"
        css={css`
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;

          height: ${cardHeight}px;
          width: inherit;

          ${!!addImage &&
            `margin: ${cardSize === 'small' ? '30px' : '45px'} 0 0 0;`}
          border: rgba(0, 0, 0, 0.1);
          border-radius: 40px;
          background: ${cardColor};
          box-shadow: 0 20px 60px -25px rgba(0, 0, 0, 0.4);

          transition: all 0.15s;

          ${navigationEnabled
            ? `
          cursor: pointer;
          transition: all 0.15s;
        
          &:hover {
            opacity: 0.8;
          }`
            : 'pointer-events: none;'}

          &:focus {
            ${getButtonFocus(
              cardColor !== '#fff' ? cardColor : '#1e2533',
              '0 20px 60px -25px rgba(0, 0, 0, 0.4)'
            )}
          }
        `}
        onClick={e => {
          if (window.dev()) {
            e.preventDefault();
            return;
          }
        }}
        onKeyUp={e => {
          if (![' ', 'Enter'].includes(e.key)) {
            return;
          }

          if (window) {
            window.location.hash = getTopicSId(topic) as string;
          }
        }}
      >
        <TopicNameContainer>
          <div
            css={css`
              padding: 5px 15px;
              font-size: 16px;
              font-family: ${font};
              color: ${textColor};
              text-align: center;
            `}
          >
            {name}
          </div>
        </TopicNameContainer>

        <div
          css={css`
            position: absolute;
            display: flex;
            flex-direction: column;
            align-items: center;
            left: 0;
            bottom: 0;
            width: 100%;
            height: ${cardSize === 'small' ? '55px' : '65px'};
          `}
        >
          <div
            css={css({
              margin: '0 0 0px 0',
              fontSize: '14px',
              color: graphColor,
            })}
          >
            {percentComplete}%
          </div>
          <ProgressBar color={graphColor} percentComplete={percentComplete} />
        </div>
      </a>

      {!!addImage && (
        <TopicIcon
          topicIcon={topicIcon}
          size={cardWidth === 235 ? 60 : 90}
          isEditing={isEditing}
          iconSelected={iconSelected}
          setIconSelected={setIconSelected}
        />
      )}
    </div>
  );
};

export default TopicPanel;
